import React, { useState, useRef, useEffect } from 'react';
import '../assets/css/formFields.css';

/**
 * A searchable dropdown that wears the same box as TextInput.
 *
 * Both use .sk-field__control, so a select and a text box sitting side by side
 * in a row line up to the pixel and light up the same way on focus. The look
 * used to be an inline style object here, which is why the field had no focus
 * or hover state at all: inline styles outrank the stylesheet, so the rules
 * already written for it could never apply.
 *
 * Behaviour is unchanged apart from keyboard support (arrows, Enter, Escape).
 */
const SelectInput = ({
  label,
  name,
  value,
  onChange,
  options = [],
  error,
  required = false,
  width = "100%",
  placeholder = "Select option",
  maxHeight = '240px',
  disabled = false
}) => {

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Store what user types
  /* Whether searchTerm is something the user TYPED, or just the label of what
     is already selected being shown back to them. The list is only filtered
     in the first case.

     Without this distinction the box filtered by its own selected label, so
     opening the dropdown showed one option — the one already chosen. It was
     worst where an option legitimately holds an empty value: the "Anyone in
     the team" choice on the Move Task dialog matched the initial empty state,
     pre-filled the box with its label, and filtered every real team member
     out of the list before the user had typed anything at all. */
  const [isTyping, setIsTyping] = useState(false);
  // Which row the keyboard is on. -1 = none.
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  /* Open upward when there is not enough room below.
   *
   * The menu was pinned to `top: 100%` and only ever grew downward, so a field
   * near the bottom of the window — or of a modal, which is where this was
   * found — ran its list straight off the edge. Two options visible out of
   * eight, and no hint the rest existed.
   *
   * Measured from the trigger against the viewport at the moment of opening,
   * and re-measured on scroll and resize, because "is there room" stops being
   * true the moment anything moves. */
  const [dropUp, setDropUp] = useState(false);

  /* Decide the direction each time it opens, and keep deciding while it is
     open. Reads the menu's real height when it has one, and falls back to the
     configured maxHeight for the very first pass before it is in the DOM. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const decide = () => {
      /* The wrap, not listRef.parentElement — that only happens to be the
         wrap while the menu stays a direct child, and this should not break
         the day somebody nests it one div deeper.

         Safe to measure even with the menu open: the menu is absolutely
         positioned, so it is out of flow and contributes nothing to the
         wrap's box. The number therefore does not move as a result of the
         decision it is feeding. */
      const trigger = dropdownRef.current;
      if (!trigger) return;

      const r = trigger.getBoundingClientRect();
      const h = listRef.current?.offsetHeight || parseInt(maxHeight, 10) || 240;

      const below = window.innerHeight - r.bottom;
      const above = r.top;

      // Flip only when it genuinely does not fit below AND there is more room
      // above — flipping into a smaller gap helps nobody.
      setDropUp(h + 12 > below && above > below);
    };

    decide();
    window.addEventListener("scroll", decide, true);
    window.addEventListener("resize", decide);
    return () => {
      window.removeEventListener("scroll", decide, true);
      window.removeEventListener("resize", decide);
    };
  }, [isOpen, maxHeight]);

  // Sync searchTerm with the selected value whenever the value prop changes
  useEffect(() => {
    const selectedOption = options.find(opt => opt.value === value);
    if (selectedOption) {
      setSearchTerm(selectedOption.label);
    } else {
      setSearchTerm("");
    }
    setIsTyping(false);
  }, [value, options]);

  /* Filter only against what was actually typed; otherwise show everything.
   *
   * Searches the meta line as well as the label. The two are one thing to the
   * person typing — they see "Mano" over "Web App · manoswasinfotech@gmail.com"
   * and will type any part of it. Matching the label alone would have quietly
   * broken searching by email the moment the address moved to the second
   * line. */
  const filteredOptions = isTyping
    ? options?.filter((opt) => {
        const q = String(searchTerm || "").toLowerCase();
        return (
          String(opt?.label ?? "").toLowerCase().includes(q) ||
          String(opt?.meta ?? "").toLowerCase().includes(q)
        );
      })
    : options;

  const toggleDropdown = () => {
    if (!disabled) {
      // Opening always starts from the full list.
      if (!isOpen) setIsTyping(false);
      setIsOpen(!isOpen);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsTyping(true);
    setActiveIndex(0);
    if (!isOpen) setIsOpen(true);

    // Optional: If you want to clear the value in the parent when user clears text
    if (e.target.value === "") {
        const fakeEvent = { target: { name: name, value: "" } };
        onChange(fakeEvent);
    }
  };

  const handleSelect = (option) => {
    // Update parent state
    const fakeEvent = { target: { name: name, value: option.value } };
    onChange(fakeEvent);

    // Update local UI
    setSearchTerm(option.label);
    setIsTyping(false);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  /* Escape and Tab close without choosing; the arrows walk the list and Enter
     takes what is highlighted. A dropdown you can only operate with a mouse
     is slow to fill a nine-field form with. */
  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Escape" || e.key === "Tab") {
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setIsTyping(false);
        setIsOpen(true);
        setActiveIndex(
          (filteredOptions || []).findIndex((o) => o.value === value),
        );
      }
      return;
    }

    const count = (filteredOptions || []).length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1 >= count ? 0 : i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? count - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = (filteredOptions || [])[activeIndex];
      if (option) handleSelect(option);
    }
  };

  // Keep the keyboard-highlighted row inside the scrolling menu.
  useEffect(() => {
    if (!isOpen || activeIndex < 0 || !listRef.current) return;
    listRef.current.children[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen]);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);

        // UX Improvement: If user typed something but didn't select,
        // revert input to the currently selected valid value
        const selectedOption = options.find(opt => opt.value === value);
        if (selectedOption) {
          setSearchTerm(selectedOption.label);
        } else {
          setSearchTerm("");
        }
        setIsTyping(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  return (
    <div className="sk-field" style={{ width }}>

      {label && (
        <label className="sk-field__label" htmlFor={name}>
          {label}
          {required && <span className="sk-field__required">*</span>}
        </label>
      )}

      <div className="sk-field__wrap" ref={dropdownRef}>

        <div
          className={[
            "sk-field__control",
            isOpen ? "is-focused" : "",
            error ? "is-invalid" : "",
            disabled ? "is-disabled" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => {
            if (disabled || isOpen) return;
            inputRef.current?.focus();
            setIsTyping(false); // clicking in shows the whole list
            setIsOpen(true);
            setActiveIndex(
              (options || []).findIndex((o) => o.value === value),
            );
          }}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        >
          <input
            id={name}
            ref={inputRef}
            type="text"
            className="sk-field__input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            autoComplete="off"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={`${name}-listbox`}
            aria-invalid={error ? "true" : undefined}
            style={{ cursor: disabled ? "not-allowed" : "text" }}
          />

          {/* Arrow Icon with pointer cursor */}
          <i
            className={`bi bi-chevron-down sk-field__affix${
              isOpen ? " sk-field__affix--open" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown();
            }}
            aria-hidden="true"
          ></i>
        </div>

        {isOpen && (
          <div
            className={`sk-field__menu${dropUp ? " sk-field__menu--up" : ""}`}
            style={{ maxHeight }}
            id={`${name}-listbox`}
            role="listbox"
            ref={listRef}
          >
            {filteredOptions?.length > 0 ? (
              filteredOptions.map((opt, i) => {
                const isSelected = value === opt.value;
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    className={[
                      "sk-field__option",
                      isSelected ? "is-selected" : "",
                      i === activeIndex ? "is-active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(opt);
                    }}
                  >
                    {/* An option can be two lines: the label, and a `meta`
                        line under it for the context that decides the choice.
                        Kept as plain data on the option rather than a render
                        prop — every caller wants the same shape, and a second
                        line of small grey text is not worth a callback. */}
                    <span className="sk-field__option-text">
                      <span className="text-truncate">{opt.label}</span>
                      {opt.meta && (
                        <span className="sk-field__option-meta">{opt.meta}</span>
                      )}
                    </span>
                    {isSelected && (
                      <i
                        className="bi bi-check2 sk-field__option-check"
                        aria-hidden="true"
                      ></i>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="sk-field__menu-empty">No results found</div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="sk-field__error">
          <i className="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default SelectInput;
